import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  AppSummarySectionConfig,
  EstimateConfirmationLabels,
  Labels,
  Renderable,
  RenderableConfig,
} from '../app.models';
import { Observable, Subject } from 'rxjs';
import { LocationFinderService } from '../services/location-finder.service';

export interface EstConfirmationData {
  amount: number;
  fees: number;
  discounts?: number;
  currency: string;
}

export interface EstConfirmationConfig extends RenderableConfig {
  amount: number;
  fees: number;
  discounts?: number;
  currency: string;
}

@Component({
  selector: 'app-estimate-confirmation',
  templateUrl: './estimate-confirmation.component.html',
  styleUrls: ['./estimate-confirmation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EstimateConfirmationComponent implements OnInit, Renderable {
  estimateConfirmLabels!: EstimateConfirmationLabels;
  locationLabels!: any;
  @Input() public sendCurrency!: string;
  @Output() public next: EventEmitter<EstConfirmationData> =
    new EventEmitter<EstConfirmationData>();
  @Output() public back: EventEmitter<void> = new EventEmitter<void>();

  private readonly _config?: EstConfirmationConfig;
  public get config() {
    return this._config!;
  }

  @Input() flow!: string;
  @Input() locale!: string;

  private _amount!: string;
  @Input() public set amount(amount: string | number) {
    if (typeof amount === 'number') {
      this._amount = amount.toFixed(2);
    } else {
      this._amount = amount && parseFloat(amount).toFixed(2);
    }
  }
  public get amount() {
    return this._amount;
  }

  public _labels: any;
  @Input() public set labels(labels: Labels) {
    this._labels = labels;
    this.estimateConfirmLabels = labels.stellar.estimateConfirmation;
    this.locationLabels = labels.stellar.location;
  }
  public get labels() {
    return this._labels;
  }

  private _totalAmountToPay!: string;
  @Input() public set totalAmountToPay(totalAmountToPay: string | number) {
    if (typeof totalAmountToPay === 'number') {
      this._totalAmountToPay = totalAmountToPay.toFixed(2);
    } else {
      this._totalAmountToPay =
        totalAmountToPay && parseFloat(totalAmountToPay).toFixed(2);
    }
  }
  public get totalAmountToPay() {
    return this._totalAmountToPay;
  }

  private _totalUSDCAmount!: string;
  @Input() public set totalUSDCAmount(totalUSDCAmount: string | number) {
    if (typeof totalUSDCAmount === 'number') {
      this._totalUSDCAmount = totalUSDCAmount.toFixed(2);
    } else {
      this._totalUSDCAmount =
        totalUSDCAmount && parseFloat(totalUSDCAmount).toFixed(2);
    }
  }
  public get totalUSDCAmount() {
    return this._totalUSDCAmount;
  }

  private _exchangeAmount!: string;
  @Input() public set exchangeAmount(exchangeAmount: string | number) {
    if (typeof exchangeAmount === 'number') {
      this._exchangeAmount = exchangeAmount.toFixed(2);
    } else {
      this._exchangeAmount =
        exchangeAmount && parseFloat(exchangeAmount).toFixed(2);
    }
  }
  public get exchangeAmount() {
    return this._exchangeAmount;
  }

  public _google: any;
  @Input() public set google(map: any) {
    this._google = map;
  }
  public get google() {
    return this._google;
  }

  private _payOutAtLocation!: string;
  @Input() public set payOutAtLocation(payOutAtLocation: string | number) {
    if (typeof payOutAtLocation === 'number') {
      this._payOutAtLocation = payOutAtLocation.toFixed(2);
    } else {
      this._payOutAtLocation =
        payOutAtLocation && parseFloat(payOutAtLocation).toFixed(2);
    }
  }
  public get payOutAtLocation() {
    return this._payOutAtLocation;
  }

  private _exchangeRate!: string;
  @Input() public set exchangeRate(exchangeRate: string | number) {
    if (typeof exchangeRate === 'number') {
      this._exchangeRate = exchangeRate.toFixed(4);
    } else {
      this._exchangeRate = exchangeRate && parseFloat(exchangeRate).toFixed(4);
    }
  }
  public get exchangeRate() {
    return this._exchangeRate;
  }

  private _fees!: string;
  @Input() public set fees(fees: string | number) {
    if (typeof fees === 'number') {
      this._fees = fees.toFixed(2);
    } else {
      this._fees = fees && parseFloat(fees).toFixed(2);
    }
  }
  public get fees() {
    return this._fees;
  }

  private _taxes!: string;
  @Input() public set taxes(taxes: string | number) {
    if (typeof taxes === 'number') {
      this._taxes = taxes.toFixed(2);
    } else {
      this._taxes = taxes && parseFloat(taxes).toFixed(2);
    }
  }
  public get taxes() {
    return this._taxes;
  }

  // private _partnerFees!: string; // TODO: not targeted for June 18th, also requirements changed for PartnerFees, so commenting out for now
  // @Input() public set partnerFees(partnerFees: string | number) {
  //   if (typeof partnerFees === 'number') {
  //     this._partnerFees = partnerFees.toFixed(2);
  //   } else {
  //     this._partnerFees = partnerFees && parseFloat(partnerFees).toFixed(2);
  //   }
  // }
  // public get partnerFees() {
  //   return this._partnerFees;
  // }

  // private _partnerTaxes!: string;
  // @Input() public set partnerTaxes(partnerTaxes: string | number) {
  //   if (typeof partnerTaxes === 'number') {
  //     this._partnerTaxes = partnerTaxes.toFixed(2);
  //   } else {
  //     this._partnerTaxes = partnerTaxes && parseFloat(partnerTaxes).toFixed(2);
  //   }
  // }
  // public get partnerTaxes() {
  //   return this._partnerTaxes;
  // }

  private _discounts?: string;
  @Input() public set discounts(discounts: string | number) {
    if (typeof discounts === 'number') {
      this._discounts = discounts.toFixed(2);
    } else {
      this._discounts = discounts && parseFloat(discounts).toFixed(2);
    }
  }
  public get discounts() {
    return this._discounts!;
  }

  private _feesCurrencyCode?: string;
  @Input() public set feesCurrencyCode(feesCurrencyCode: string) {
    this._feesCurrencyCode = feesCurrencyCode;
  }
  public get feesCurrencyCode() {
    return this._feesCurrencyCode!;
  }

  // private _partnerFeesCurrencyCode?: string // TODO: not targeted for June 18th, also requirements changed for PartnerFees, so commenting out for now
  // @Input() public set partnerFeesCurrencyCode(partnerFeesCurrencyCode: string) {
  //   this._partnerFeesCurrencyCode = partnerFeesCurrencyCode
  // }
  // public get partnerFeesCurrencyCode() {
  //   return this._partnerFeesCurrencyCode!
  // }

  private _taxesCurrencyCode?: string;
  @Input() public set taxesCurrencyCode(taxesCurrencyCode: string) {
    this._taxesCurrencyCode = taxesCurrencyCode;
  }
  public get taxesCurrencyCode() {
    return this._taxesCurrencyCode!;
  }

  // private _partnerTaxesCurrencyCode?: string; // TODO: not targeted for June 18th, also requirements changed for PartnerFees, so commenting out for now
  // @Input() public set partnerTaxesCurrencyCode(partnerTaxesCurrencyCode: string) {
  //   this._partnerTaxesCurrencyCode = partnerTaxesCurrencyCode;
  // }
  // public get partnerTaxesCurrencyCode() {
  //   return this._partnerTaxesCurrencyCode!;
  // }

  private _currency?: string;
  @Input() public set currency(currency: string) {
    this._currency = currency;
  }
  public get currency() {
    return this._currency!;
  }

  private _payOutAtLocationCurrency?: string;
  @Input() public set payOutAtLocationCurrency(
    payOutAtLocationCurrency: string
  ) {
    this._payOutAtLocationCurrency = payOutAtLocationCurrency;
  }
  public get payOutAtLocationCurrency() {
    return this._payOutAtLocationCurrency!;
  }

  public get total() {
    return (
      ((this._amount && parseFloat(this._amount)) || 0) +
      ((this._fees && parseFloat(this._fees)) || 0) -
      ((this._discounts && parseFloat(this._discounts)) || 0)
    ).toFixed(2);
  }

  public get summarySectionConfig() {
    // const items = []
    // No need to display as per Yassir for now, so commented for now
    // if (this._discounts) {
    //   items.push({
    //     name: this.estimateConfirmLabels.discount,
    //     value: `- ${this._discounts}`,
    //     sendCurrency: this.flow === 'withdraw' ? this.sendCurrency : this.currency
    //   })
    // }

    // if(this.payOutAtLocation && this.flow === "deposit") {
    //   items.push({
    //     name: this.estimateConfirmLabels.toPayAtLocation,
    //     value: this.payOutAtLocation,
    //     sendCurrency: this.payOutAtLocationCurrency,
    //     isBold: true,
    //   })
    // }

    const summarySections = [];
    const exchangeAmount = {
      name: this.estimateConfirmLabels.exchangeAmount,
      value: this.exchangeAmount,
      sendCurrency: this.sendCurrency,
      isBold: false,
      displayMinus: false,
    };
    const fees = {
      name: this.estimateConfirmLabels.fees,
      value: this._fees,
      sendCurrency: this.feesCurrencyCode,
      isBold: false,
      displayMinus: this.flow === 'withdraw' && Number(this._fees) > 0,
    };
    const taxes = {
      name: this.estimateConfirmLabels.taxes,
      value: this._taxes,
      sendCurrency: this.taxesCurrencyCode,
      isBold: false,
      displayMinus: this.flow === 'withdraw' && Number(this._taxes) > 0,
    };
    // const partnerFees: itemSummarySection = { // TODO: not targeted for June 18th, also requirements changed for PartnerFees, so commenting out for now
    //   name: 'Partner Fees', // Todo: hardcoded label. Will be updated when language is finalized
    //   value: this.partnerFees,
    //   sendCurrency: this.partnerFeesCurrencyCode,
    //   isBold: false,
    //   displayMinus: this.flow === 'withdraw' && Number(this.partnerFees) > 0,
    // }

    // const partnerTaxes: itemSummarySection = { // TODO: not targeted for June 18th, also requirements changed for PartnerFees, so commenting out for now
    //   name: 'Partner Taxes', // Todo: hardcoded label. Will be updated when language is finalized
    //   value: this.partnerTaxes,
    //   sendCurrency: this.partnerTaxesCurrencyCode,
    //   isBold: false,
    //   displayMinus: this.flow === 'withdraw' && Number(this.partnerTaxes) > 0,
    // }

    if (this.flow === 'withdraw') {
      summarySections.push(fees);
      summarySections.push(taxes);
      summarySections.push(exchangeAmount);
    } else {
      summarySections.push(exchangeAmount);
      summarySections.push(fees);
      summarySections.push(taxes);
      // if (!isNaN(partnerFees.value) && partnerFees.value !== null && typeof partnerFees.value !== "undefined") { summarySections.push(partnerFees); } // TODO: not targeted for June 18th, also requirements changed for PartnerFees, so commenting out for now
      // if (!isNaN(partnerTaxes.value) && partnerTaxes.value !== null && typeof partnerTaxes.value !== 'undefined') { summarySections.push(partnerTaxes); } // TODO: not targeted for June 18th, also requirements changed for PartnerFees, so commenting out for now
    }

    return {
      title: {
        name: this.estimateConfirmLabels.costEstimate,
      },
      items: summarySections,
    } as AppSummarySectionConfig;
  }

  getExchangeRate() {
    if (this.flow === 'withdraw') {
      return `1.00 USDC = ${this.exchangeRate} ${this.currency}`;
    }
    return '1.00 USD = 1.00 USDC';
  }

  private _onInit$: Subject<void> = new Subject<void>();
  public readonly onInit$: Observable<void> = this._onInit$.asObservable();

  constructor(public locationFinderService: LocationFinderService) {}

  ngOnInit(): void {
    if (this._config) {
      this.amount = this._config.amount;
      this.fees = this._config.fees;
      this.discounts = this._config?.discounts!;
      this._currency = this._config.currency;
    }

    this._onInit$.next();
    this._onInit$.complete();
  }

  public confirmFees(): void {
    this.next.emit({
      amount: (this._amount && parseFloat(this._amount)) || undefined!,
      currency: this._currency!,
      fees: (this._fees && parseFloat(this._fees)) || undefined!,
      discounts: (this._discounts && parseFloat(this._fees)) || undefined,
    });
  }
  public onBack(): void {
    this.back.emit();
  }
}
